import { useState } from 'react';
import { formatUkPhone, normaliseUkMobile } from '@tikkas/shared';
import { COIN, Svg } from '../art/icons.js';
import { api } from '../lib/api.js';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Scroll, TopBar } from '../components/ui.js';
import { WalletButtons, walletsHere, walletsIssued } from '../components/Wallet.js';
import { APP_BUILD, APP_ENV } from '../lib/platform.js';
import { cardBackErrorText } from '../lib/joining.js';

/*
 * Get my card back (CLAUDE.md §4, docs/23 §3.7): a new phone, or the pass deleted. The customer types their mobile and
 * the code we text, and the same card goes back in their Wallet: same code, same points and credit. No sign-in is made;
 * the text code is asked for because the card spends store credit. Linked from the join page and the counter sticker.
 */

type Found = { firstName: string; codeLast4: string | null; apple: string | null; google: string | null };

export function CardBack() {
  const s = useStore();
  const { navigate, back } = useNav();
  const [step, setStep] = useState<'phone' | 'code' | 'done'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [testCode, setTestCode] = useState<string | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; noAccount: boolean } | null>(null);
  const number = normaliseUkMobile(phone);

  const start = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!number) return setErr({ text: 'Enter a UK mobile number, for example 07700 900123', noAccount: false });
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; testCode?: string }>('/api/card/recover/start', { body: { phone: number } });
      setTestCode(r.testCode ?? null);
      setCode('');
      setStep('code');
    } catch (e2) {
      setErr(cardBackErrorText(e2));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await api<{ ok: boolean } & Found>('/api/card/recover/verify', { body: { phone: number, code } });
      setFound({ firstName: r.firstName, codeLast4: r.codeLast4, apple: r.apple, google: r.google });
      setStep('done');
    } catch (e2) {
      setErr(cardBackErrorText(e2));
    } finally {
      setBusy(false);
    }
  };

  // Only while the server can give a pass back (the switch on, and Apple or Google set up): otherwise the page says so and sends no text.
  const wallet = walletsIssued(s);
  const offer = found ? walletsHere(s, { apple: !!found.apple, google: !!found.google }) : [];
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="" onBack={back} />
      <div className="col" style={{ gap: 10, alignItems: 'center', textAlign: 'center' }}>
        <Svg html={COIN(40)} />
        <h1 className="display" style={{ fontSize: 30 }}>{step === 'done' && found ? `Here's your card${found.firstName ? `, ${found.firstName}` : ''}` : step === 'code' ? 'Check your texts' : 'Get my card back'}</h1>
        <p className="soft" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, maxWidth: 320 }}>
          {step === 'done' ? `The same card${found?.codeLast4 ? ` ending ${found.codeLast4}` : ''}, with your points and store credit. Add it to your phone's wallet again.`
            : step === 'code' ? `We sent a 6-digit code to ${formatUkPhone(number ?? phone)}.`
            : "New phone, or deleted the pass? Type your mobile number and the code we text you, and your Tikkas card goes back in your wallet. Same card, same points."}
        </p>
      </div>

      {!wallet ? (
        <div className="card empty" role="status"><b>Wallet cards open soon</b><span className="small muted">Your card is always in the app, on the Card tab.</span>
          <button type="button" className="yellow" style={{ minHeight: 50, padding: '0 24px' }} onClick={() => navigate('/card', { root: true })}>Open the Card tab</button></div>
      ) : step === 'done' ? (
        <div className="col" style={{ gap: 12 }}>
          {offer.length ? <WalletButtons urls={{ apple: found?.apple ?? null, google: found?.google ?? null }} /> : <div className="card empty" role="status"><b>Wallet cards aren't ready yet</b><span className="small muted">Your card is in the app, on the Card tab, once you sign in.</span></div>}
          <button type="button" className="plain" onClick={() => navigate(s.me ? '/card' : '/join?next=/card', { root: !!s.me })}>{s.me ? 'Open the Card tab' : 'Sign in to the app instead'}</button>
        </div>
      ) : step === 'phone' ? (
        <form className="col" style={{ gap: 14 }} onSubmit={start}>
          <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="backphone">Mobile number
            <span className="phonein"><span>+44</span><input id="backphone" className="txt-in" type="tel" inputMode="tel" placeholder="7700 900123" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus /></span></label>
          {err && <CardBackError err={err} />}
          <button type="submit" className="yellow" disabled={busy}>{busy ? 'Sending…' : 'Send my code by text'}</button>
          <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>We ask for a code because the card can pay with your store credit.</p>
        </form>
      ) : (
        <form className="col" style={{ gap: 14 }} onSubmit={verify}>
          {/* Off production the server shows the code when it sent no text, as on the join page. */}
          {testCode && APP_ENV !== 'production' && (APP_BUILD
            ? <div className="testtools"><span>Test code: <b style={{ fontSize: 20, letterSpacing: '.2em' }}>{testCode}</b></span></div>
            : <div className="testtools"><span className="staging">Staging test mode · no text was sent</span><span>Your code is <b style={{ fontSize: 20, letterSpacing: '.2em' }}>{testCode}</b></span></div>)}
          <label className="col" style={{ gap: 6, fontSize: 13, fontWeight: 600 }} htmlFor="backcode">6-digit code
            <input id="backcode" className="txt-in" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus style={{ letterSpacing: '.3em', fontSize: 22, textAlign: 'center' }} /></label>
          {err && <CardBackError err={err} />}
          <button type="submit" className="yellow" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Get my card'}</button>
          <button type="button" className="link" style={{ alignSelf: 'center' }} onClick={() => { setStep('phone'); setCode(''); setErr(null); }}>Wrong number? Go back</button>
        </form>
      )}
    </Scroll>
  );
}

function CardBackError({ err }: { err: { text: string; noAccount: boolean } }) {
  const { navigate } = useNav();
  return (
    <div className="col" style={{ gap: 8 }}>
      <p className="err" role="alert" style={{ margin: 0 }}>{err.text}</p>
      {err.noAccount && <button type="button" className="btn2" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/join', { replace: true })}>Join the Rewards Club</button>}
    </div>
  );
}
